import {
  Button,
  Modal,
  Box,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  Backdrop,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EscalatorWarningRoundedIcon from "@mui/icons-material/EscalatorWarningRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import MaleRoundedIcon from "@mui/icons-material/MaleRounded";
import FemaleRoundedIcon from "@mui/icons-material/FemaleRounded";
import TransgenderRoundedIcon from "@mui/icons-material/TransgenderRounded";

// ---- palette -----------------------------------------------------------
// A calm clinical teal paired with a warm coral accent for actions/errors.
const palette = {
  teal: "#4B49AC",
  tealDark: "#37357D",
  tealSoft: "#EEEDF9",
  ink: "#1F2937",
  slate: "#6B7280",
  line: "#E6E9ED",
  bg: "#FAFBFC",
  coral: "#FF6B57",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    fontSize: "14px",
    "& fieldset": { borderColor: palette.line },
    "&:hover fieldset": { borderColor: palette.teal },
    "&.Mui-focused fieldset": { borderColor: palette.teal, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "14px",
    "&.Mui-focused": { color: palette.teal },
  },
  "& .MuiInputAdornment-root svg": {
    fontSize: "19px",
    color: palette.slate,
  },
};

const SectionLabel = ({ icon, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "14px" }}>
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: palette.tealSoft,
        color: palette.teal,
        "& svg": { fontSize: "16px" },
      }}
    >
      {icon}
    </Box>
    <span
      style={{
        fontSize: "12px",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: palette.slate,
      }}
    >
      {children}
    </span>
  </Box>
);

const PatientRegistrationForm = ({
  open,
  handleClose,
  formData,
  handleInputChange,
  handleCreatePatient,
  isEditClicked,
  validationErrors,
}) => {
  const handleGenderChange = (_e, value) => {
    if (value) handleInputChange({ target: { name: "gender", value } });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 300, sx: { backgroundColor: "rgba(17, 24, 39, 0.55)" } } }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "760px",
            maxWidth: "94vw",
            maxHeight: "88vh",
            overflowY: "auto",
            bgcolor: palette.bg,
            borderRadius: "18px",
            boxShadow: "0 24px 60px -12px rgba(15, 23, 42, 0.35)",
            outline: "none",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              gap: "14px",
              padding: "22px 28px",
              background: `linear-gradient(135deg, ${palette.teal} 0%, ${palette.tealDark} 100%)`,
              borderRadius: "18px 18px 0 0",
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                backgroundColor: "rgba(255,255,255,0.16)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PersonRoundedIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <h3 style={{ margin: 0, color: "#fff", fontSize: "18px", fontWeight: 700 }}>
                {isEditClicked ? "Edit Patient" : "Register a New Patient"}
              </h3>
              <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.75)", fontSize: "12.5px" }}>
                {isEditClicked
                  ? "Update the patient's details below"
                  : "Enter the patient and guardian details to get started"}
              </p>
            </Box>
            <IconButton
              onClick={handleClose}
              sx={{
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.12)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ padding: "26px 28px 28px" }}>
            {/* Patient details */}
            <SectionLabel icon={<PersonRoundedIcon />}>Patient Details</SectionLabel>
            <Grid container spacing={2} sx={{ mb: "26px" }}>
              <Grid size={7}>
                <TextField
                  fullWidth
                  label="Patient Name"
                  size="small"
                  name="patient_name"
                  value={formData.patient.patient_name}
                  onChange={handleInputChange}
                  sx={fieldSx}
                  error={!!validationErrors.patient_name}
                  helperText={validationErrors.patient_name}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={5}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  size="small"
                  name="date_of_birth"
                  value={formData.patient.date_of_birth}
                  onChange={handleInputChange}
                  sx={fieldSx}
                  error={!!validationErrors.date_of_birth}
                  helperText={validationErrors.date_of_birth}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarMonthRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid size={12}>
                <span style={{ fontSize: "12.5px", fontWeight: 600, color: palette.ink }}>
                  Gender
                </span>
                <ToggleButtonGroup
                  value={formData.patient.gender}
                  exclusive
                  onChange={handleGenderChange}
                  sx={{
                    display: "flex",
                    mt: "6px",
                    gap: "10px",
                    "& .MuiToggleButtonGroup-grouped": {
                      border: `1px solid ${palette.line} !important`,
                      borderRadius: "10px !important",
                      flex: 1,
                      textTransform: "none",
                      fontSize: "13.5px",
                      fontWeight: 600,
                      color: palette.slate,
                      gap: "6px",
                      padding: "8px 0",
                      backgroundColor: "#fff",
                    },
                    "& .Mui-selected": {
                      backgroundColor: `${palette.tealSoft} !important`,
                      color: `${palette.tealDark} !important`,
                      borderColor: `${palette.teal} !important`,
                    },
                  }}
                >
                  <ToggleButton value="male">
                    <MaleRoundedIcon fontSize="small" /> Male
                  </ToggleButton>
                  <ToggleButton value="female">
                    <FemaleRoundedIcon fontSize="small" /> Female
                  </ToggleButton>
                  <ToggleButton value="other">
                    <TransgenderRoundedIcon fontSize="small" /> Other
                  </ToggleButton>
                </ToggleButtonGroup>
                {validationErrors.gender && (
                  <p style={{ color: "#D32F2F", fontSize: "0.75rem", margin: "6px 0 0" }}>
                    {validationErrors.gender}
                  </p>
                )}
              </Grid>
            </Grid>

            {/* Guardian & contact */}
            <SectionLabel icon={<EscalatorWarningRoundedIcon />}>
              Guardian &amp; Contact Information
            </SectionLabel>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Parent / Guardian Name"
                  size="small"
                  name="name"
                  value={formData.user.name}
                  onChange={handleInputChange}
                  sx={fieldSx}
                  error={!!validationErrors.name}
                  helperText={validationErrors.name}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EscalatorWarningRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  size="small"
                  name="phone_number"
                  value={formData.user.phone_number}
                  onChange={handleInputChange}
                  sx={fieldSx}
                  error={!!validationErrors.phone_number}
                  helperText={validationErrors.phone_number}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CallRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Email"
                  size="small"
                  name="email"
                  value={formData.user.email}
                  onChange={handleInputChange}
                  sx={fieldSx}
                  error={!!validationErrors.email}
                  helperText={validationErrors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlineRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Address"
                  size="small"
                  name="address"
                  value={formData.user.address}
                  onChange={handleInputChange}
                  sx={fieldSx}
                  error={!!validationErrors.address}
                  helperText={validationErrors.address}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HomeRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* Footer */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                mt: "30px",
                pt: "20px",
                borderTop: `1px solid ${palette.line}`,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  borderColor: palette.line,
                  color: palette.slate,
                  padding: "8px 22px",
                  "&:hover": { borderColor: palette.slate, backgroundColor: "#F3F4F6" },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCreatePatient}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  padding: "8px 24px",
                  backgroundColor: palette.teal,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: palette.tealDark, boxShadow: "none" },
                }}
              >
                {isEditClicked ? "Save Changes" : "Register Patient"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default PatientRegistrationForm;
